import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { DropZoneUpload } from "@/components/ui/drop-zone-upload";
import {
  Trash2, X, Check, Globe, BookOpen, PenTool, RefreshCw, ImageIcon, Archive,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { ImageGenTabProps } from "./types";

export function ReferencesTab(props: ImageGenTabProps) {
  const {
    entity, currentImageUrl, generatedPreview, onImageRemoved,
    seedImageUrl, setSeedImageUrl, handleFileUpload, handleSeedUpload,
    uploadingSeed, showMediaPicker, setShowMediaPicker, existingMedia,
    creativityLevel, setCreativityLevel, setActiveTab, setDrawingInitialImage,
    webReferenceImages, webReferenceThumbnails, selectedRefImages,
    codexEntityImages, drawingDataUrl, cachedImages, setShowCachePopup,
    renderRefImageGrid,
  } = props;

  const existingReferenceUrls = Array.from(
    new Set((existingMedia || []).map((m) => m.image_url).filter((u): u is string => !!u))
  );

  return (
    <div className="space-y-4">
      {/* Current Image */}
      {(currentImageUrl || entity.image_url) && !generatedPreview && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Current Image</label>
          <div className="relative rounded-lg overflow-hidden border border-border group aspect-video">
            <img src={currentImageUrl || entity.image_url!} alt={entity.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            {onImageRemoved && (
              <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="destructive" size="sm" className="h-6 px-2 text-[10px] gap-1" onClick={() => onImageRemoved()}>
                  <Trash2 className="w-3 h-3" /> Remove
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seed Upload */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-foreground">Seed Image <span className="text-muted-foreground font-normal">(optional)</span></label>
        {seedImageUrl ? (
          <div className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30 border border-border">
            <div className="relative w-14 h-14 rounded-lg overflow-hidden border border-primary/30 ring-1 ring-primary/20 shrink-0">
              <img src={seedImageUrl} alt="Seed" className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground">Reference uploaded</p>
              <div className="flex items-center gap-2 mt-1">
                <Button variant="ghost" size="sm" className="text-[11px] h-6 px-2 text-destructive hover:text-destructive" onClick={() => setSeedImageUrl(null)}>
                  <X className="w-3 h-3 mr-1" /> Remove
                </Button>
                <label className="inline-flex items-center gap-1 h-6 px-2 text-[11px] bg-background hover:bg-muted border border-border rounded-md cursor-pointer transition-colors">
                  <RefreshCw className="w-3 h-3" /> Replace
                  <input type="file" accept="image/*" className="hidden" onChange={handleSeedUpload} />
                </label>
                <Button variant="ghost" size="sm" className="text-[11px] h-6 px-2 gap-1" onClick={() => { setDrawingInitialImage(seedImageUrl); setActiveTab("draw"); }}>
                  <PenTool className="w-3 h-3" /> Edit
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <DropZoneUpload
              onFileSelected={handleFileUpload}
              accept="image/*"
              uploading={uploadingSeed}
              label="Drop, paste, or click to upload"
              sublabel="PNG, JPG, WebP"
              validateFile={(f) => f.type.startsWith("image/") ? null : "Only image files are supported"}
            />
            {(existingMedia || []).length > 0 && (
              <Button variant="outline" size="sm" className="gap-1.5 text-xs w-full" onClick={() => setShowMediaPicker(!showMediaPicker)}>
                <ImageIcon className="w-3 h-3" /> Select from Media Gallery
              </Button>
            )}
          </div>
        )}

        {showMediaPicker && (existingMedia || []).length > 0 && (
          <div className="rounded-lg border border-border bg-muted/20 p-2">
            <p className="text-[10px] text-muted-foreground mb-2">Select from existing media:</p>
            <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto">
              {(existingMedia || []).map(m => (
                <div key={m.id} className={cn("relative aspect-square rounded-md overflow-hidden border cursor-pointer transition-all hover:border-primary/50",
                  seedImageUrl === m.image_url ? "border-primary ring-1 ring-primary/30" : "border-border"
                )} onClick={() => { setSeedImageUrl(m.image_url); setShowMediaPicker(false); }}>
                  <img src={m.image_url} alt={m.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  {seedImageUrl === m.image_url && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center"><Check className="w-4 h-4 text-primary" /></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {seedImageUrl && (
          <div className="space-y-2 p-3 rounded-lg bg-muted/20 border border-border">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Creativity Level</label>
              <span className="text-[11px] text-primary font-medium">
                {creativityLevel <= 20 ? "Very Faithful" : creativityLevel <= 40 ? "Faithful" : creativityLevel <= 60 ? "Balanced" : creativityLevel <= 80 ? "Creative" : "Very Creative"}
              </span>
            </div>
            <Slider value={[creativityLevel]} onValueChange={(v) => setCreativityLevel(v[0])} min={0} max={100} step={10} className="w-full" />
          </div>
        )}
      </div>

      {/* Existing media as references */}
      {existingReferenceUrls.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5 text-primary" />
            {existingReferenceUrls.length} image{existingReferenceUrls.length > 1 ? "s" : ""} from media gallery
            {selectedRefImages.size > 0 && <Badge variant="secondary" className="ml-1 text-[9px] h-4 px-1.5">{selectedRefImages.size}/3 selected</Badge>}
          </label>
          {renderRefImageGrid(existingReferenceUrls, existingReferenceUrls)}
        </div>
      )}

      {/* Found References */}
      {webReferenceImages.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-primary" />
            {webReferenceImages.length} web reference{webReferenceImages.length > 1 ? "s" : ""}
            {selectedRefImages.size > 0 && <Badge variant="secondary" className="ml-1 text-[9px] h-4 px-1.5">{selectedRefImages.size}/3 selected</Badge>}
          </label>
          {renderRefImageGrid(webReferenceImages, webReferenceThumbnails)}
        </div>
      )}

      {codexEntityImages.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5 text-primary" />
            {codexEntityImages.length} codex image{codexEntityImages.length > 1 ? "s" : ""}
          </label>
          {renderRefImageGrid(codexEntityImages, codexEntityImages,
            <div className="bg-primary/80 rounded-sm p-0.5"><BookOpen className="w-2 h-2 text-primary-foreground" /></div>
          )}
        </div>
      )}

      {/* Drawing as reference */}
      {drawingDataUrl && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <PenTool className="w-3.5 h-3.5 text-primary" /> Drawing sketch
          </label>
          <div className="relative w-full aspect-square rounded-lg overflow-hidden border-2 border-primary/30">
            <img src={drawingDataUrl} alt="Drawing" className="w-full h-full object-contain bg-muted/20" />
            <div className="absolute top-1.5 right-1.5 flex gap-1">
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1 bg-background/80" onClick={() => setActiveTab("draw")}>
                <PenTool className="w-3 h-3" /> Edit
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1 bg-background/80 text-destructive" onClick={() => props.setDrawingDataUrl(null)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {cachedImages.length > 0 && (
        <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs h-7" onClick={() => setShowCachePopup(true)}>
          <Archive className="w-3 h-3" /> Last Found Images ({cachedImages.length})
        </Button>
      )}
    </div>
  );
}
